import { PaymentAccountOverdraftQuestion } from '@hb/domain/steps/financial-obligation';
import { FinancialObligationsStoreService } from '@hb/modules/financial-obligations/financial-obligations-store.service';
import { FieldSubject } from '@hb/domain/field-subject';
import { money } from '@hb/builder';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';

@Injectable({
    providedIn: 'root'
})
export class PaymentAccountOverdraftStoreService extends AbstractStoreInjectOption<PaymentAccountOverdraftQuestion> implements OnDestroy {
    protected readonly store = inject(FinancialObligationsStoreService);
    protected readonly lastQuestion = PaymentAccountOverdraftQuestion.SUMMARY;

    private paymentOverdraftSubscription: Subscription;
    private readonly totalCreditAmountSubject: FieldSubject<Money>;
    private readonly paymentAccountOverdraftTipFinishedSubject: FieldSubject<boolean>;

    constructor() {
        super();
        this.init();

        const initialValues: Money = this.getInitialValues();
        this.totalCreditAmountSubject = new FieldSubject<Money>(initialValues);
        this.paymentAccountOverdraftTipFinishedSubject = new FieldSubject(null);

        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.paymentOverdraftSubscription.unsubscribe();
        this.activeQuestionSubject.unsubscribe();
        this.totalCreditAmountSubject.unsubscribe();
        this.paymentAccountOverdraftTipFinishedSubject.unsubscribe();
    }

    getTotalCreditAmount(): Money {
        return this.totalCreditAmountSubject.value;
    }

    setTotalCreditAmount(amount: string): void {
        this.totalCreditAmountSubject.next(money(amount));
    }

    setOverdraftTipFinished(): void {
        this.paymentAccountOverdraftTipFinishedSubject.next(true);
    }

    getInitialValues(): Money {
        return this.store.getFinancialObligationState()?.payment_account_overdraft_amount;
    }

    update(): void {
        this.store.setPaymentAccountOverdraft(this.totalCreditAmountSubject.value);
    }

    private subscriptions(): void {
        this.subscribePaymentAccountOverdraftOption();
        this.subscribeFieldSubjects();
    }

    private subscribePaymentAccountOverdraftOption() {
        this.paymentOverdraftSubscription = this.store.getFinancialObligationsOptions().subscribe((options) => {
            if (!options.paymentAccountOverdraft) {
                this.totalCreditAmountSubject.next(null);
                this.paymentAccountOverdraftTipFinishedSubject.next(null);
            }
        });
    }

    private subscribeFieldSubjects() {
        this.totalCreditAmountSubject.asObservable().subscribe(() => this.update());
        this.paymentAccountOverdraftTipFinishedSubject.asObservable().subscribe(() => this.nextQuestion());
    }
}
